import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaUserCircle, FaTag } from "react-icons/fa";

const tags = ["Sustainability", "Green SNF", "SNF"];
const related = [
  { title: "Family Engagement: Building Trust in SNFs", link: "/blog/family-engagement" },
];

const Blog8: React.FC = () => (
  <motion.section initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, type: "spring" }} className="max-w-3xl mx-auto py-12 px-4">
    {/* Banner/Featured Image */}
    <div className="w-full h-48 md:h-64 bg-gradient-to-r from-blue-200 via-blue-100 to-white rounded-2xl mb-8 flex items-end justify-start relative overflow-hidden">
      <img src="https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=800&q=80" alt="Sustainability" className="absolute inset-0 w-full h-full object-cover object-center opacity-60" />
      <div className="relative z-10 p-6">
        <span className="bg-blue-700 text-white px-4 py-1 rounded-full text-xs font-semibold shadow">Blog</span>
      </div>
    </div>
    {/* Title & Meta */}
    <h1 className="text-3xl md:text-4xl font-extrabold mb-2 text-blue-800">Sustainable Practices in Skilled Nursing Facilities</h1>
    <div className="flex items-center gap-3 mb-4">
      <FaUserCircle className="text-2xl text-blue-400" />
      <span className="text-gray-700 font-medium">RoomRounds Team</span>
      <span className="text-gray-400">|</span>
      <span className="text-gray-500 text-sm">June 2024</span>
    </div>
    {/* Tags */}
    <div className="flex flex-wrap gap-2 mb-6">
      {tags.map(tag => (
        <span key={tag} className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold"><FaTag /> {tag}</span>
      ))}
    </div>
    {/* Main Content */}
    <article className="prose prose-blue max-w-none mb-16 bg-white rounded-xl shadow p-8 prose-headings:mt-12 prose-headings:mb-6 prose-p:mb-6 prose-ul:mb-8 prose-ol:mb-8 prose-li:mb-3 prose-h2:pt-4 prose-h3:pt-2">
      <p>Environmental sustainability is becoming a priority for SNFs. Adopting green practices not only benefits the planet but can also improve operational efficiency and resident well-being. In this comprehensive guide, we'll explore actionable strategies, real-world examples, and the future of sustainability in skilled nursing facilities.</p>
      <hr className="my-8 border-blue-100" />
      <h2>Why Sustainability Matters in SNFs</h2>
      <p>Healthcare facilities are significant consumers of energy and resources. Sustainable practices help reduce costs, meet regulatory requirements, and create a healthier environment for residents and staff.</p>
      <ul>
        <li>Reduces energy and water usage</li>
        <li>Minimizes waste and pollution</li>
        <li>Improves indoor air quality</li>
        <li>Enhances facility reputation and marketability</li>
      </ul>
      <hr className="my-8 border-blue-100" />
      <h2>Green Initiatives for SNFs</h2>
      <ul>
        <li><strong>Energy Efficiency:</strong> Upgrade lighting and HVAC systems, use smart thermostats, and insulate buildings.</li>
        <li><strong>Waste Reduction:</strong> Implement recycling and composting programs, reduce single-use plastics.</li>
        <li><strong>Water Conservation:</strong> Use low-flow fixtures, monitor usage, and fix leaks promptly.</li>
        <li><strong>Eco-Friendly Purchasing:</strong> Choose sustainable cleaning products and supplies.</li>
        <li><strong>Green Landscaping:</strong> Plant native species and reduce chemical use.</li>
      </ul>
      <p><strong>Example:</strong> At GreenLeaf SNF, switching to LED lighting and low-flow toilets reduced utility costs by 20% in one year.</p>
      <hr className="my-8 border-blue-100" />
      <h2>How to Start a Sustainability Program</h2>
      <ol>
        <li>Conduct a sustainability audit to identify opportunities.</li>
        <li>Set measurable goals for improvement.</li>
        <li>Engage staff and residents in green initiatives.</li>
        <li>Track progress and celebrate successes.</li>
        <li>Share results with families and the community.</li>
      </ol>
      <p><strong>Tip:</strong> Form a "Green Team" of staff and residents to champion sustainability efforts.</p>
      <hr className="my-8 border-blue-100" />
      <h2>Case Study: Reducing Waste in SNFs</h2>
      <p>Sunrise SNF launched a recycling and composting program, diverting 40% of waste from landfills and saving $5,000 annually on disposal costs.</p>
      <ul>
        <li>Staff training on recycling protocols</li>
        <li>Resident participation in composting</li>
        <li>Monthly progress reports and recognition</li>
      </ul>
      <hr className="my-8 border-blue-100" />
      <h2>Common Challenges and Solutions</h2>
      <ul>
        <li><strong>Upfront Costs:</strong> Seek grants, rebates, and phased implementation to manage expenses.</li>
        <li><strong>Staff Buy-In:</strong> Provide education and incentives for participation.</li>
        <li><strong>Resident Engagement:</strong> Involve residents in planning and celebrate their contributions.</li>
        <li><strong>Measuring Impact:</strong> Use simple tracking tools and share results regularly.</li>
      </ul>
      <hr className="my-8 border-blue-100" />
      <h2>Future Trends in Sustainable SNFs</h2>
      <ul>
        <li><strong>Renewable Energy:</strong> Solar panels and green power purchasing.</li>
        <li><strong>Smart Building Technology:</strong> Automated systems for lighting, HVAC, and water use.</li>
        <li><strong>Green Certifications:</strong> LEED and ENERGY STAR recognition for healthcare facilities.</li>
        <li><strong>Community Partnerships:</strong> Collaborate with local organizations for greater impact.</li>
      </ul>
      <hr className="my-8 border-blue-100" />
      <h2>Frequently Asked Questions</h2>
      <h3>How can we fund sustainability projects?</h3>
      <p>Look for utility rebates, government grants, and partnerships with local businesses or nonprofits.</p>
      <h3>What are easy first steps?</h3>
      <p>Switch to LED lighting, start a recycling program, and educate staff on energy-saving habits.</p>
      <h3>How do we measure success?</h3>
      <p>Track utility bills, waste diversion rates, and staff/resident participation in green initiatives.</p>
      <h3>Can sustainability improve resident health?</h3>
      <p>Yes! Better air quality, less chemical exposure, and access to green spaces all support well-being.</p>
      <hr className="my-8 border-blue-100" />
      <h2>Checklist: Is Your SNF Going Green?</h2>
      <ul>
        <li>Energy and water audits completed</li>
        <li>Recycling and composting programs in place</li>
        <li>Staff and resident engagement in green efforts</li>
        <li>Progress tracked and shared</li>
        <li>Recognition for sustainability achievements</li>
      </ul>
      <hr className="my-8 border-blue-100" />
      <h2>Conclusion</h2>
      <p>Sustainable practices are good for the planet, your residents, and your bottom line. By starting small and building momentum, your SNF can become a leader in environmental stewardship.</p>
      <p>RoomRounds is here to help you every step of the way. <Link to="/contact" className="text-blue-600 hover:underline">Contact us</Link> for sustainability resources and support.</p>
      <hr className="my-8" />
      <h2>About RoomRounds</h2>
      <p>RoomRounds is a leading provider of digital compliance and sustainability solutions for skilled nursing facilities. Our platform is trusted by SNFs nationwide to improve quality, efficiency, and environmental impact.</p>
      <h2>References</h2>
      <ol>
        <li>U.S. Environmental Protection Agency (EPA) Healthcare Sustainability Resources</li>
        <li>Centers for Medicare & Medicaid Services (CMS) Green Initiatives</li>
        <li>RoomRounds Customer Success Stories</li>
      </ol>
    </article>
    {/* Related Posts */}
    <div className="mb-12 pt-8 border-t border-blue-100">
      <h3 className="text-lg font-bold text-blue-700 mb-3">Related Posts</h3>
      <ul className="flex flex-col gap-2">
        {related.map(r => (
          <li key={r.link}>
            <Link to={r.link} className="text-blue-600 hover:underline font-medium">{r.title}</Link>
          </li>
        ))}
      </ul>
    </div>
    <Link to="/resources" className="text-blue-600 hover:underline">← Back to Resources</Link>
  </motion.section>
);

export default Blog8; 